import React, { useCallback, useEffect, useState } from 'react';
import {
  Bookmark,
  Category,
  NavigationItemType,
  Report,
} from 'core/slices/models';
import { Accordion } from 'react-bootstrap';
import ReportNavItem from 'components/VertNavBar/components/ReportNavItem';

const getNavChildUniqueId = (child: Report | Category): string => {
  if (child.nodeType === NavigationItemType.REPORT) {
    return `report:${child.id}`;
  }

  return `category:${(child as Category).id || (child as Category).categoryName}`;
};

interface CategoryNavItemsProps {
  elem: Category;
  onReportClick?: (report: Report) => void;
  onBookmarkClick?: (bookmark: Bookmark) => void;
  onEditBookmarkClick?: (bookmark?: Bookmark) => void;
  activeReport?: Report;
  activeBookmarkId?: number;
  getReportState?: () => Promise<string>;
}

const CategoryNavItem: React.FC<CategoryNavItemsProps> = ({
  elem,
  onReportClick,
  onBookmarkClick,
  onEditBookmarkClick,
  activeReport,
  activeBookmarkId,
  getReportState,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleReportClick = (report: Report) => {
    onReportClick && onReportClick(report);
  };

  const onSelectBookMarkClickMenu = (bookmark: Bookmark) => {
    onBookmarkClick && onBookmarkClick(bookmark);
  };

  const onAddBookmarkClickMenu = () => {
    onEditBookmarkClick && onEditBookmarkClick();
  };

  const onEditBookmarkClickMenu = (bookmark: Bookmark) => {
    onEditBookmarkClick && onEditBookmarkClick(bookmark);
  };

  //TODO if report is active also expand category
  const isCategoryActive = useCallback(() => {
    if (!activeReport) return false;
    return elem.children?.some(
      child =>
        child.nodeType === NavigationItemType.REPORT &&
        child.id === activeReport.id
    );
  }, [activeReport, elem.children]);

  useEffect(() => {
    setIsExpanded(isCategoryActive());
  }, [isCategoryActive]);

  const uniqueChildren = (elem.children || []).filter(
    (child, index, children) =>
      index ===
      children.findIndex(
        candidate =>
          getNavChildUniqueId(candidate) === getNavChildUniqueId(child)
      )
  );

  return (
    <Accordion.Item
      eventKey={elem.categoryName}
      className={isExpanded ? 'active' : ''}
    >
      <Accordion.Header> {elem.categoryName}</Accordion.Header>
      <Accordion.Body>
        <div className="category-reports">
          {uniqueChildren.map(child => {
            switch (child.nodeType) {
              case NavigationItemType.REPORT:
                return (
                  <ReportNavItem
                    key={getNavChildUniqueId(child)}
                    elem={child as Report}
                    onReportClick={handleReportClick}
                    onBookmarkClick={onSelectBookMarkClickMenu}
                    onEditBookmarkClick={onEditBookmarkClickMenu}
                    onAddBookmarkClick={onAddBookmarkClickMenu}
                    activeReport={activeReport}
                  />
                );
              case NavigationItemType.CATEGORY:
                return (
                  <CategoryNavItem
                    key={getNavChildUniqueId(child)}
                    elem={child as Category}
                    onReportClick={onReportClick}
                    onBookmarkClick={onBookmarkClick}
                    activeReport={activeReport}
                    activeBookmarkId={activeBookmarkId}
                    getReportState={getReportState}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default CategoryNavItem;
